/**
 * Detects the "magic-link email is missing" error thrown by the auth data
 * layer when localStorage no longer holds the pending email (typical when
 * the user clicks the link on a different device or browser). Keeps the
 * fragile string check confined to one place so the auth layer can change
 * its message without breaking the callback page.
 *
 * @param {unknown} error - the value thrown by completeMagicLinkSignIn
 * @returns {boolean}
 */
const isMissingEmailError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return error.message.toLowerCase().includes('missing')
  }

  if (typeof error === 'string') {
    return error.toLowerCase().includes('missing')
  }

  return false
}

/**
 * Pulls a user-friendly message out of an unknown caught value. Avoids
 * leaking stack traces or "[object Object]" into the UI.
 *
 * @param {unknown} error
 * @param {string} fallback - shown when the error has no extractable message
 * @returns {string}
 */
const describeError = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message !== '') {
    return error.message
  }

  if (typeof error === 'string' && error !== '') {
    return error
  }

  return fallback
}

/**
 * Loose email shape check — Firebase will reject anything genuinely
 * malformed downstream, this just stops the form submitting on obvious
 * typos like a missing @.
 *
 * @param {string} value
 * @returns {boolean}
 */
const looksLikeEmail = (value: string): boolean => {
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u
  return pattern.test(value)
}

export { isMissingEmailError, describeError, looksLikeEmail }
